<template>
    <WidgetSection
        ref="WidgetSection"
        :loaded="loaded"
        :loading="loading"
        :unavailable="unavailable"
        :defaultToggle="defaultToggle"
        @loading-start="handleLoadingStart"
        loadingText="common.loading_1"
        :paywall="paywallActive"
        :cssClass="cssClassWidgetSection"
    >
        <template v-slot:section-title>
            <div class="title">
                {{ $t('components.prospect_property.name')}}
            </div>
        </template>

        <template v-slot:section-content>
            <div class="row row-content">
                <div class="section">
                    <ul class="specs">
                        <li v-if="property.details?.gebruiksdoel">
                            <div class="title">
                                {{ $t('components.prospect_property.type')}}
                            </div>
                            <div class="content capitalize">
                                {{ property.details.gebruiksdoel }}
                            </div>
                        </li>
                        <li v-if="property.details?.energielabel">
                            <div class="title">
                                {{ $t('components.prospect_property.energy_label')}}
                            </div>
                            <div class="content">
                                {{ property.details.energielabel }}
                            </div>
                        </li>
                        <li v-if="property.details?.bouwjaar">
                            <div class="title">
                                {{ $t('components.prospect_property.year_construction')}}
                            </div>
                            <div class="content">
                                {{ property.details.bouwjaar }}
                            </div>
                        </li>
                        <li v-if="property.details?.oppervlakte">
                            <div class="title">
                                {{ $t('components.prospect_property.size_floor')}}
                            </div>
                            <div class="content">
                                {{ formatNumber(property.details.oppervlakte, '', 'm²') }}
                            </div>
                        </li>
                        <li v-if="property.details?.grondoppervlakte">
                            <div class="title">
                                {{ $t('components.prospect_property.size_land')}}
                            </div>
                            <div class="content">
                                {{ formatNumber(property.details.grondoppervlakte, '', 'm²') }}
                            </div>
                        </li>
                        <li v-if="property.details?.woz">
                            <div class="title">
                                {{ $t('components.prospect_property.woz')}}
                            </div>
                            <div class="content">
                                {{ formatNumber(property.details.woz, '€', '') }} <span v-if="property.details.woz_year" class="woz-year">({{ property.details.woz_year }})</span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </template>
    </WidgetSection>
</template>

<script>
    import { useProspectStore } from '@/stores/prospect';
    import { useAccountStore } from '@/stores/account';
    import { fetchUrl } from '@/services/ppApi';
    import WidgetSection from '@/components/sections/WidgetSection';
    import { formatNumber } from '@/utils/formatting';
    import i18n from "@/plugins/i18n";
    import { useToastStore } from '@/stores/utils/toast';

    export default {
        name: "ProspectSameAddress",
        components: {
            WidgetSection
        },
        props: {
            auto: {
                type: Boolean,
                default: false
            },
            defaultToggle: {
                type: Boolean,
                default: false
            },
            paywall: {
                type: Boolean,
                default: false
            },
            cssClassWidgetSection: {
                type: String,
                required: false
            },
            showLoadingToasts: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                loading: false,
                loaded: false,
                abortController: null,
                timeoutId: null,
                isActive: true
            }
        },
        computed: {
            cheapmium() {
                return useAccountStore().product.includes('_pch');
            },
            paywallActive() {
                return this.paywall && (useAccountStore().paywall || this.cheapmium);
            },
            unavailable() {
                return !this.property || (this.loaded && (!this.property.details || !!Object.keys(this.property.details).length < 1 || (!this.property.details?.gebruiksdoel && !this.property.details?.energielabel && !this.property.details?.bouwjaar && !this.property.details?.oppervlakte && !this.property.details?.grondoppervlakte && !this.property.details?.woz)));
            },
            property() {
                return useProspectStore().property;
            },
        },
        methods: {
            formatNumber,
            async fetchProperty() {
                if (!this.property || (this.property.api_urls && this.property.api_urls.length < 1) || this.loaded) {
                    return;
                }

                if (this.abortController) {
                    this.abortController.abort();
                    this.abortController = null;
                }

                this.abortController = new AbortController();
                this.loading = true;

                try {
                    const response = await fetchUrl(this.property.api_urls[0], this.abortController.signal);
                    this.property.details = response.data.property;

                    if(this.showLoadingToasts && this.isActive) {
                        if(this.property?.details && Object.keys(this.property.details).length > 0) {
                            useToastStore().addToast(i18n.global.t(`toasts.success.prospect_property`), 'success');
                        } else {
                            useToastStore().addToast(i18n.global.t(`toasts.error.prospect_property`), 'error'); 
                        }
                    }
                } catch (error) {
                    if (error?.code !== 'ERR_CANCELED') {
                        this.property?.details ? this.property.details = null : '';
                    }
                } finally {
                    this.loading = false;
                    this.loaded = true;
                    this.abortController = null;
                }
            },
            async handleLoadingStart() {
                if (!this.paywallActive && !this.loaded) {
                    await this.fetchProperty();

                    if(!this.unavailable && this.$refs.WidgetSection) {
                        this.$refs.WidgetSection.toggleSection();
                    }
                }
            }
        },
        mounted() {
            if(!this.paywallActive && !this.loaded && (this.auto || this.defaultToggle)) {
                this.loading = true;
                this.timeoutId = setTimeout(() => {
                    this.fetchProperty();
                }, 300);
            }
        },
        unmounted() {
            this.isActive = false;

            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
        }
    }
</script>